/**
 * Heading extraction utilities for blog content
 *
 * Functions to extract heading information from DOM elements for table of contents.
 */

import type { Heading } from '@/types/blog';
import { generateHeadingId } from './utils';

/**
 * Extract headings from a DOM element
 *
 * Finds all h2 and h3 elements within the container, generates IDs for headings
 * without IDs, and returns an array of heading data.
 *
 * This function:
 * - Queries for h2 and h3 elements within the container
 * - Generates IDs for headings that don't have them using `generateHeadingId()`
 * - Sets the generated ID on the DOM element for anchor linking
 * - Returns structured heading data with id, text, and level
 *
 * @param container - DOM element containing headings (e.g., `.blog-content`)
 * @returns Array of extracted headings with id, text, and level
 *
 * @example
 * ```typescript
 * const blogContent = document.querySelector('.blog-content');
 * const headings = extractHeadingsFromDOM(blogContent);
 * // Returns: [{ id: 'introduction', text: 'Introduction', level: 2 }, ...]
 * ```
 *
 * @see {@link generateHeadingId} - ID generation utility
 * @see {@link Heading} - Heading type definition
 */
export function extractHeadingsFromDOM(container: Element | null): Heading[] {
  if (!container) {
    return [];
  }

  const headingElements = container.querySelectorAll('h2, h3');
  const headings: Heading[] = [];
  const seenBaseIds = new Set<string>(); // Track base IDs to prevent duplicates in TOC
  const idCounts = new Map<string, number>(); // Track counts for making DOM IDs unique

  headingElements.forEach((heading) => {
    // Skip headings that are inside links (e.g., RelatedGuides component link cards)
    // These are not real content headings and shouldn't be in the TOC
    if (heading.closest('a')) {
      return;
    }

    // Skip headings inside RelatedGuides section (additional safety check)
    if (heading.closest('.related-guides-section')) {
      return;
    }

    // Skip headings inside component sections that shouldn't be in TOC
    // These components render after MDX and cause TOC to jump around
    if (
      heading.closest('.key-takeaways-section') ||
      heading.closest('.related-topics-section') ||
      heading.closest('.statistics-section')
    ) {
      return;
    }

    let baseId = heading.id;

    // Generate base ID from text if not already set
    if (!baseId) {
      const text = heading.textContent || '';
      baseId = generateHeadingId(text);
    }

    // If base ID is empty or invalid, skip this heading
    if (!baseId) {
      return;
    }

    // Make DOM ID unique if we've seen this base ID before
    // This ensures each heading gets a unique DOM ID even if content has duplicate text
    let domId = baseId;
    if (idCounts.has(baseId)) {
      const count = (idCounts.get(baseId) || 0) + 1;
      idCounts.set(baseId, count);
      domId = `${baseId}-${count}`;
    } else {
      idCounts.set(baseId, 0);
    }

    // Set the unique ID on the DOM element (ensures valid HTML)
    heading.id = domId;

    // Only add heading to TOC if we haven't seen this base ID before
    // This prevents duplicate headings in the TOC while keeping DOM IDs unique
    if (!seenBaseIds.has(baseId)) {
      seenBaseIds.add(baseId);
      headings.push({
        id: domId, // Use the unique DOM ID for TOC linking
        text: heading.textContent || '',
        level: heading.tagName === 'H2' ? 2 : 3,
      });
    }
  });

  return headings;
}
